import { useLayoutEffect, useMemo, useRef } from "react";
import Helper from "./Helper";
import { LocResources } from "./locResources";
import { EventHub, IEventHub } from "./eventHub";
import {
    buildRdsCallbackUrl,
    RdsCallbackValidationFailure,
} from "./rdsRedirection";
import { ReactComponent as Spinner } from "./Spinner.svg";
import "./DeviceFingerprintPage.css";

const DFP_SCRIPT_BASE = "https://df.cfp.microsoft.com/mdt.js";
const POST_DELAY_MS = 2500;
const MAX_TIMEOUT_MS = 6000;
const DFP_COMPLETE_MESSAGE = "dfp-fpt-invoked";

export const DeviceFingerprintTelemetryEvents = {
    requestReceived: "DeviceFingerprint_Request_Received",
    invalidRequest: "DeviceFingerprint_Invalid_Request",
    invocationAttempted: "DeviceFingerprint_Invocation_Attempted",
    invocationFailed: "DeviceFingerprint_Invocation_Failed",
    timeout: "DeviceFingerprint_Timeout",
    redirect: "DeviceFingerprint_Redirect",
} as const;

export type DeviceFingerprintValidationFailure =
    | "MissingGreenId"
    | RdsCallbackValidationFailure;

type DeviceFingerprintValidationResult =
    | { redirectUrl: string; failureReason?: never }
    | { redirectUrl: null; failureReason: DeviceFingerprintValidationFailure };

export interface IDeviceFingerprintPageProps {
    env: string;
    greenId: string;
    sessionId: string;
    telemetry?: IEventHub;
    // Keeps production navigation intact while allowing tests to verify telemetry flush order.
    navigate?: (url: string) => void;
}

const replaceLocation = (url: string): void => window.location.replace(url);

export function validateDeviceFingerprintRequest(
    sessionId: string,
    greenId: string,
    env: string
): DeviceFingerprintValidationResult {
    if (!greenId.trim()) {
        return { redirectUrl: null, failureReason: "MissingGreenId" };
    }

    const callbackResult = buildRdsCallbackUrl(sessionId, env, { greenId });
    if (callbackResult.callbackUrl === null) {
        return {
            redirectUrl: null,
            failureReason: callbackResult.failureReason,
        };
    }

    return { redirectUrl: callbackResult.callbackUrl };
}

export function buildFingerprintFrameHtml(greenId: string): string {
    const scriptUrl = `${DFP_SCRIPT_BASE}?session_id=${encodeURIComponent(greenId)}`;

    return `<!DOCTYPE html><html><head>`
        + `<meta name="referrer" content="no-referrer">`
        + `<script src="${scriptUrl}"></script>`
        + `<script>window.onload=function(){`
        + `var outcome='Unavailable';`
        + `try{if(window.dfp&&typeof window.dfp.doFpt==='function'){window.dfp.doFpt(document);outcome='Invoked';}}`
        + `catch(e){outcome='Exception';}`
        + `parent.postMessage({type:'${DFP_COMPLETE_MESSAGE}',outcome:outcome},'*');`
        + `};</script>`
        + `</head><body></body></html>`;
}

function DeviceFingerprintPage(props: IDeviceFingerprintPageProps) {
    const locResources = useMemo(
        () => new LocResources(Helper.getLanguageParam()),
        []
    );
    const frameRef = useRef<HTMLIFrameElement>(null);
    const redirectTimerRef = useRef<number>();
    const maxTimerRef = useRef<number>();
    const scheduleRedirectRef = useRef<() => void>(() => undefined);
    const hasRedirectedRef = useRef(false);
    const invocationTrackedRef = useRef(false);
    const startTimeRef = useRef(Date.now());
    const telemetry = useMemo(
        () => props.telemetry || new EventHub({
            pageContext: { pageName: "DeviceFingerprint" },
            partnerContext: { partnerId: "rds", env: props.env },
        }),
        [props.env, props.telemetry]
    );
    const validationResult = validateDeviceFingerprintRequest(
        props.sessionId,
        props.greenId,
        props.env
    );
    const redirectUrl = validationResult.redirectUrl;
    const navigate = props.navigate || replaceLocation;

    // Install the message listener before the browser can dispatch events from a fast srcDoc iframe.
    useLayoutEffect(() => {
        telemetry.trackEvent(DeviceFingerprintTelemetryEvents.requestReceived, {
            environment: props.env,
        });

        if (!redirectUrl) {
            telemetry.trackEvent(DeviceFingerprintTelemetryEvents.invalidRequest, {
                environment: props.env,
                reason: validationResult.failureReason,
            });
            telemetry.flush();
            return;
        }

        const elapsedMs = () => Date.now() - startTimeRef.current;
        const redirect = (reason: "FingerprintSignal" | "MaxTimeout") => {
            if (!hasRedirectedRef.current) {
                hasRedirectedRef.current = true;

                if (reason === "MaxTimeout") {
                    telemetry.trackEvent(DeviceFingerprintTelemetryEvents.timeout, {
                        durationMs: elapsedMs(),
                        environment: props.env,
                    });
                }

                telemetry.trackEvent(DeviceFingerprintTelemetryEvents.redirect, {
                    durationMs: elapsedMs(),
                    environment: props.env,
                    reason,
                });
                // Navigation unloads this short-lived page, so send queued events first.
                telemetry.flush();
                navigate(redirectUrl);
            }
        };
        const scheduleRedirect = () => {
            if (redirectTimerRef.current === undefined) {
                redirectTimerRef.current = window.setTimeout(
                    () => redirect("FingerprintSignal"),
                    POST_DELAY_MS
                );
            }
        };
        scheduleRedirectRef.current = scheduleRedirect;
        const handleMessage = (event: MessageEvent) => {
            if (event.source === frameRef.current?.contentWindow
                && event.data?.type === DFP_COMPLETE_MESSAGE) {
                if (!invocationTrackedRef.current) {
                    invocationTrackedRef.current = true;
                    const outcome = event.data?.outcome;
                    const eventName = outcome === "Invoked"
                        ? DeviceFingerprintTelemetryEvents.invocationAttempted
                        : DeviceFingerprintTelemetryEvents.invocationFailed;
                    telemetry.trackEvent(eventName, {
                        durationMs: elapsedMs(),
                        environment: props.env,
                        reason: outcome === "Invoked" ? undefined : outcome || "Unknown",
                    });
                }
                scheduleRedirect();
            }
        };

        window.addEventListener("message", handleMessage);
        maxTimerRef.current = window.setTimeout(
            () => redirect("MaxTimeout"),
            MAX_TIMEOUT_MS
        );

        return () => {
            window.removeEventListener("message", handleMessage);
            scheduleRedirectRef.current = () => undefined;
            if (redirectTimerRef.current !== undefined) {
                window.clearTimeout(redirectTimerRef.current);
            }
            if (maxTimerRef.current !== undefined) {
                window.clearTimeout(maxTimerRef.current);
            }
        };
    }, [navigate, props.env, telemetry, redirectUrl, validationResult.failureReason]);

    if (!redirectUrl) {
        return null;
    }

    const loading = locResources.getByName("DeviceFingerprintLoading");

    return (
        <main className="device-fingerprint-page">
            <div
                role="status"
                aria-live="polite"
                aria-label={loading}
            >
                <Spinner
                    className="device-fingerprint-spinner"
                    aria-hidden="true"
                    focusable="false"
                />
            </div>
            <iframe
                ref={frameRef}
                className="device-fingerprint-frame"
                title="Device fingerprint"
                aria-hidden="true"
                tabIndex={-1}
                onLoad={() => scheduleRedirectRef.current()}
                srcDoc={buildFingerprintFrameHtml(props.greenId)}
            />
        </main>
    );
}

export default DeviceFingerprintPage;
